import React, { useEffect } from "react";
import { Modal, Form, Input, Button, message, InputNumber } from "antd";
import { useCreateCustomerMutation } from "../../Services";
import { modalTitle } from "../AppUi";

interface CustomerModalProps {
  open: boolean;
  onCancel: () => void;
  onSuccess?: (newCustomerId: number, name: string, credit_limit?: number) => void;
  initialValues?: {
    name?: string;
    phone?: string;
    email?: string;
    address?: string;
  };
}

export const CustomerModal: React.FC<CustomerModalProps> = ({
  open,
  onCancel,
  onSuccess,
  initialValues,
}) => {
  const [form] = Form.useForm();

  useEffect(() => {
    if (open && initialValues) {
      form.setFieldsValue(initialValues);
    }
  }, [open, initialValues, form]);
  const createCustomerMutation = useCreateCustomerMutation();

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  const handleFinish = (values: any) => {
    createCustomerMutation.mutate(values, {
      onSuccess: (res) => {
        if (res?.success) {
          message.success("Customer created successfully.");
          onSuccess?.(
            res.id,
            res.name || form.getFieldValue("name"),
            res?.credit_limit,
          );
          form.resetFields();
          onCancel();
        } else {
          message.error(res?.message || "Failed to create customer.");
        }
      },
      onError: () => {
        message.error("Failed to create customer.");
      },
    });
  };

  return (
    <Modal
      title={modalTitle("Add New Customer")}
      open={open}
      onCancel={handleCancel}
      footer={null}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleFinish}
        className="pt-2"
      >
        <Form.Item
          name="name"
          label={
            <span className="text-xs font-semibold text-gray-600">
              Full Name
            </span>
          }
          rules={[{ required: true, message: "Please enter customer name." }]}
        >
          <Input placeholder="Enter full name" size="middle" />
        </Form.Item>

        <Form.Item
          name="phone"
          label={
            <span className="text-xs font-semibold text-gray-600">
              Phone Number
            </span>
          }
          rules={[
            { required: true, message: "Please enter phone number." },
            {
              pattern: /^[0-9+-\s()]*$/,
              message: "Please enter a valid phone number.",
            },
          ]}
        >
          <Input placeholder="Enter phone number" size="middle" />
        </Form.Item>

        <Form.Item
          name="email"
          label={
            <span className="text-xs font-semibold text-gray-600">
              Email Address (Optional)
            </span>
          }
          rules={[
            { type: "email", message: "Please enter a valid email address." },
          ]}
        >
          <Input placeholder="Enter email address" size="middle" />
        </Form.Item>

        <Form.Item
          name="address"
          label={
            <span className="text-xs font-semibold text-gray-600">
              Physical Address (Optional)
            </span>
          }
        >
          <Input.TextArea placeholder="Enter address details" rows={3} />
        </Form.Item>

        <Form.Item
          name="credit_limit"
          label={
            <span className="text-xs font-semibold text-gray-600">
              Credit Limit (Optional)
            </span>
          }
          rules={[
            {
              type: "number",
              min: 0,
              message: "Credit limit must be a positive number.",
            },
          ]}
        >
          <InputNumber
            placeholder="Enter credit limit"
            className="w-full"
            min={0}
            style={{ width: "100%" }}
          />
        </Form.Item>

        <div className="flex justify-end gap-2 pt-4 border-t border-gray-100">
          <Button
            size="middle"
            onClick={handleCancel}
            disabled={createCustomerMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            size="middle"
            type="primary"
            htmlType="submit"
            loading={createCustomerMutation.isPending}
          >
            Save Customer
          </Button>
        </div>
      </Form>
    </Modal>
  );
};
